import React from 'react'
import { DayResult } from '../types'
import { buildMonthWeeks } from '../lib/calendarUtils'
import { MONTH_NAMES, WEEKDAY_LABELS, describeDay, formatMoney } from '../lib/format'
import { ragBg, targetClasses, targetReplacesBg } from '../lib/ragStyles'
import { toISODate } from '../lib/dateRules'

interface Props {
  year: number
  results: Map<string, DayResult>
  currencySymbol: string
  onSelectMonth: (month0: number) => void
  onSelectDay: (iso: string) => void
  todayIso: string
}

function MiniMonth({
  year,
  month0,
  results,
  currencySymbol,
  onSelectMonth,
  onSelectDay,
  todayIso,
}: {
  year: number
  month0: number
} & Omit<Props, 'year'>) {
  const weeks = buildMonthWeeks(year, month0)

  return (
    <div className="bg-white border border-line rounded p-2 sm:p-3">
      <button
        onClick={() => onSelectMonth(month0)}
        className="font-display text-[15px] text-ink mb-2 hover:text-accent transition-colors"
      >
        {MONTH_NAMES[month0]}
      </button>
      <div className="grid grid-cols-7 gap-[3px] mb-1">
        {WEEKDAY_LABELS.map((w) => (
          <div key={w} className="text-[9px] text-ink/70 font-sans text-center">
            {w[0]}
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-[3px]">
        {weeks.map((week, wi) => (
          <div key={wi} className="grid grid-cols-7 gap-[3px]">
            {week.map((day, di) => {
              if (!day) return <div key={di} className="aspect-square" />
              const iso = toISODate(day)
              const result = results.get(iso)
              const isToday = iso === todayIso
              const hit = result?.targetHits[0]
              return (
                <button
                  key={di}
                  aria-label={describeDay(day, result, currencySymbol)}
                  disabled={!result}
                  onClick={result ? () => onSelectDay(iso) : undefined}
                  title={
                    result
                      ? `${iso}: ${formatMoney(result.balance, currencySymbol)}${
                          hit ? ` — ${hit.name} reached` : ''
                        }`
                      : `${iso}: before starting balance date`
                  }
                  className={`aspect-square rounded-sm text-[9px] font-mono flex items-center justify-center
                    ${!result ? 'bg-line/40 text-ink/25 cursor-not-allowed' : hit && targetReplacesBg(hit.style) ? '' : ragBg[result.rag]}
                    ${hit ? targetClasses(hit.style) : ''}
                    ${isToday ? 'ring-1 ring-accent' : ''}
                    ${result ? 'hover:ring-1 hover:ring-ink/30 transition-shadow' : ''}`}
                >
                  {day.getDate()}
                </button>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}

export default function YearView(props: Props) {
  return (
    // Two across on a phone rather than one: a year you have to scroll through
    // twelve screens of isn't a year view.
    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3">
      {Array.from({ length: 12 }, (_, m) => (
        <MiniMonth key={m} month0={m} {...props} />
      ))}
    </div>
  )
}
